/*
 * ॐ नमः शिवाय
 * ಓಂ ನಮಃ ಶಿವಾಯ
 * ఓం నమః శివాయ
 * ஓம் நம சிவாய
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * feedModel
 * ---------
 * Pure functions behind the athlete feed (/feed). No React, no router, no
 * network — everything here takes plain rows and returns plain values, so the
 * whole model is unit-tested without rendering.
 *
 * Rows come from the tables the UpVibe Android app syncs to
 * (SyncRepository / ActivityRecordMapper in the upvibe project):
 *   activity           — one row per recorded workout
 *   activity_gps_point — the route, ordered by `sequence`
 *   activity_split     — per-km splits
 *
 * Field notes that shape the readers below:
 *   - activity_type and privacy are written with Enum.name(), so they arrive
 *     UPPERCASE ("RUN", "PRIVATE"); they are lower-cased on read.
 *   - activity_meta is a JSON object on the wire but may come back as a string.
 *   - started_at is UTC, returned without a zone; it is shown in the viewer's zone.
 */

import { parseStoredUtc } from '../../utils/zonedDateTime';

// `plural` is the Activity Breakdown row label — UpVibe's label_runs, label_walks, … strings.
export const TYPE_META = {
  run:     { label: 'Run',     plural: 'Runs',    icon: 'directions_run',   measure: 'pace',   gps: true },
  walk:    { label: 'Walk',    plural: 'Walks',   icon: 'directions_walk',  measure: 'pace',   gps: true },
  hike:    { label: 'Hike',    plural: 'Hikes',   icon: 'hiking',           measure: 'pace',   gps: true },
  cycling: { label: 'Cycling', plural: 'Cycling', icon: 'directions_bike',  measure: 'speed',  gps: true },
  ride:    { label: 'Ride',    plural: 'Rides',   icon: 'two_wheeler',      measure: 'speed',  gps: true },
  drive:   { label: 'Drive',   plural: 'Drives',  icon: 'directions_car',   measure: 'speed',  gps: true },
  swim:    { label: 'Swim',    plural: 'Swims',   icon: 'pool',             measure: 'pace100', gps: false },
  yoga:    { label: 'Yoga',    plural: 'Yoga',    icon: 'self_improvement', measure: 'none',   gps: false },
};

/** Filter chips, in the order the UpVibe History screen shows them. */
export const TYPE_ORDER = ['run', 'walk', 'swim', 'hike', 'yoga', 'cycling', 'ride', 'drive'];

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DAY_MS = 24 * 60 * 60 * 1000;

const pad2 = (n) => String(n).padStart(2, '0');
const num = (v) => (v === null || v === undefined || v === '' || Number.isNaN(Number(v)) ? null : Number(v));

export function typeMeta(type) {
  return TYPE_META[type] || { label: 'Activity', icon: 'monitoring', measure: 'pace', gps: true };
}

/** activity_meta arrives as an object, a JSON string, or not at all. */
export function parseMeta(value) {
  if (!value) return null;
  if (typeof value === 'object') return value;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (e) {
    return null;
  }
}

/**
 * Timestamps come back as naive UTC ("2026-09-24T00:42:00", no zone), because
 * that is what the column holds. new Date() would read that in the browser's
 * zone and shift every activity by the viewer's offset, so the shared
 * parseStoredUtc (utils/zonedDateTime) reads it as UTC.
 */
function parseDate(value) {
  if (!value) return null;
  return parseStoredUtc(value);
}

/** One `activity` row → the shape every feed component reads. */
export function normalizeActivity(row) {
  if (!row || row.id === undefined || row.id === null) return null;
  const type = String(row.activity_type || '').toLowerCase();
  const rpe = num(row.rpe);
  const privacy = String(row.privacy || 'private').toLowerCase();
  return {
    id: row.id,
    type,
    title: (row.title || '').trim(),
    notes: row.notes || '',
    startedAt: parseDate(row.started_at),
    durationSec: Math.max(0, Math.round(num(row.duration_seconds) || 0)),
    distanceKm: Math.max(0, (num(row.distance_meters) || 0) / 1000),
    kcal: num(row.calories_burned),
    elevGain: num(row.elevation_gain),
    avgPaceSecPerKm: num(row.average_pace_seconds_per_km),
    totalSteps: num(row.total_steps),
    targetSteps: num(row.target_steps),
    targetDistanceKm: num(row.target_distance_km),
    rpe: rpe && rpe >= 1 && rpe <= 10 ? Math.round(rpe) : null,
    privacy: ['private', 'followers', 'public'].includes(privacy) ? privacy : 'private',
    meta: parseMeta(row.activity_meta),
    // When the row reached the server: the app's synced_at if it sent one,
    // else the server's own created_at (UpVibe does not send synced_at).
    syncedAt: parseDate(row.synced_at) || parseDate(row.created_at),
    dataSource: row.data_source ? String(row.data_source).toLowerCase() : null,
  };
}

/** The most recent sync time across activities, or null when none is known. */
export function lastSynced(list) {
  return list.reduce((latest, a) => (a.syncedAt && (!latest || a.syncedAt > latest) ? a.syncedAt : latest), null);
}

const IMPORT_SOURCES = {
  garmin: 'Garmin', strava: 'Strava', fitbit: 'Fitbit', polar: 'Polar', suunto: 'Suunto',
  coros: 'COROS', apple_health: 'Apple Health', google_fit: 'Google Fit', health_connect: 'Health Connect',
};

/**
 * Where the activity came from, from data_source. UpVibe leaves it empty, so
 * empty (or "upvibe") reads as recorded in UpVibe; a known wearable reads as
 * imported; anything else (test seeds, unknown sources) gets no label rather
 * than a guess.
 */
// The 'upvibe' check stays: it is the data_source value the mobile app writes,
// not text anyone sees.
export function sourceLabel(a) {
  // if (!a.dataSource || a.dataSource === 'upvibe') return 'Recorded with UpVibe';
  if (!a.dataSource || a.dataSource === 'upvibe') return 'Recorded with AthloHive';
  const name = IMPORT_SOURCES[a.dataSource];
  return name ? `Imported from ${name}` : null;
}

/** Rows from table/fetch → normalized activities, newest first. */
export function normalizeActivities(rows) {
  if (!Array.isArray(rows)) return [];
  return rows
    .map(normalizeActivity)
    .filter((a) => a && a.startedAt)
    .sort((x, y) => y.startedAt - x.startedAt);
}

/**
 * UpVibe saves default titles as null and builds "Afternoon Walk" at display
 * time (ActivityTitleFormatter.defaultTitle), so an untitled row gets the same
 * time-of-day prefix here from the local start hour: before 12 Morning, before
 * 17 Afternoon, else Evening. Without a start time it is just the type label.
 */
export function displayTitle(a) {
  if (a.title) return a.title;
  const label = typeMeta(a.type).label;
  if (!a.startedAt) return label;
  const hour = a.startedAt.getHours();
  const timeOfDay = hour < 12 ? 'Morning' : hour < 17 ? 'Afternoon' : 'Evening';
  return `${timeOfDay} ${label}`;
}

export function hasDistance(a) {
  return typeMeta(a.type).measure !== 'none' && a.distanceKm > 0;
}

export function hasRoute(a) {
  return typeMeta(a.type).gps && a.distanceKm > 0;
}

export function formatDuration(sec) {
  const s = Math.max(0, Math.round(sec || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const x = s % 60;
  return h ? `${h}:${pad2(m)}:${pad2(x)}` : `${pad2(m)}:${pad2(x)}`;
}

/** "3h 56m" / "45m" — for totals, where seconds are noise. */
export function formatHoursMinutes(sec) {
  const s = Math.max(0, Math.round(sec || 0));
  const h = Math.floor(s / 3600);
  const m = Math.round((s % 3600) / 60);
  if (h && m === 60) return `${h + 1}h 0m`;
  return h ? `${h}h ${m}m` : `${m}m`;
}

/** 304 → 5'04" */
export function formatPace(secPerUnit) {
  if (!secPerUnit || !Number.isFinite(secPerUnit)) return '—';
  const total = Math.round(secPerUnit);
  return `${Math.floor(total / 60)}'${pad2(total % 60)}"`;
}

export function formatKm(km, digits = 2) {
  return (km || 0).toFixed(digits);
}

/**
 * Pace for foot sports, speed for wheels, pace per 100 m for swims. Null only
 * for types with no distance (yoga); a session that never moved still gets a
 * value — 0.0 km/h, or "—" for a pace — as UpVibe's detail screen shows it.
 * Pace prefers the value the app saved (it guards stationary sessions against
 * a phantom pace) and only falls back to time ÷ distance when none was saved.
 * Swim pace is the saved per-km pace ÷ 10 in whole seconds, exactly as the app
 * derives it, so both read the same; a swim with no saved pace shows "—".
 */
export function paceOrSpeed(a) {
  const { measure } = typeMeta(a.type);
  if (measure === 'none') return null;
  const moved = a.distanceKm > 0 && a.durationSec > 0;
  if (measure === 'speed') {
    const kmh = moved ? a.distanceKm / (a.durationSec / 3600) : 0;
    return { label: 'Speed', value: kmh.toFixed(1), unit: 'km/h' };
  }
  if (measure === 'pace100') {
    const per100 = a.avgPaceSecPerKm > 0 ? Math.floor(a.avgPaceSecPerKm / 10) : null;
    return { label: 'Pace', value: formatPace(per100), unit: '/100m' };
  }
  const pace = a.avgPaceSecPerKm > 0 ? a.avgPaceSecPerKm : moved ? a.durationSec / a.distanceKm : null;
  return { label: 'Pace', value: formatPace(pace), unit: '/km' };
}

/** Types that can earn calories — the app hides the Calories row for the rest. */
const NO_CALORIE_TYPES = ['yoga', 'ride', 'drive'];

// Elevation means nothing on the water or the mat — the app hides it for both.
export const NO_ELEVATION_TYPES = ['swim', 'yoga'];

/**
 * The stat grid on the activity detail view: [[label, value, unit]], mirroring
 * UpVibe's ActivityDetailFragment. Distance, pace/speed and calories always
 * show — as 0 / "—" when nothing was recorded — so a metric never silently
 * vanishes; a steps-goal activity swaps distance/pace for steps. Swim distance
 * reads in whole metres, as in the app.
 */
export function detailStats(a, maxElev = null) {
  const cells = [];
  const stepsGoal = hasStepsGoal(a);
  const hasDistanceMetric = typeMeta(a.type).measure !== 'none';
  if (hasDistanceMetric && !stepsGoal) {
    cells.push(a.type === 'swim'
      ? ['Distance', Math.round(a.distanceKm * 1000).toLocaleString('en-IN'), 'm']
      : ['Distance', formatKm(a.distanceKm), 'km']);
  }
  cells.push(['Moving time', formatDuration(a.durationSec), '']);
  const ps = stepsGoal ? null : paceOrSpeed(a);
  if (ps) cells.push([ps.label === 'Speed' ? 'Avg speed' : 'Avg pace', ps.value, ps.unit]);
  const st = stepsInfo(a);
  if (st) {
    const count = st.steps.toLocaleString('en-IN');
    cells.push(['Steps', st.target ? `${count} / ${st.target.toLocaleString('en-IN')}` : count, '']);
  }
  const pct = goalPercent(a);
  if (pct !== null) cells.push(['Goal', pct, '%']);
  if (a.type === 'yoga') {
    const y = yogaInfo(a);
    cells.push(['Style', y.style, '']);
    if (y.intensity) cells.push(['Intensity', y.intensity, '']);
  }
  if (!NO_CALORIE_TYPES.includes(a.type)) cells.push(['Calories', Math.round(a.kcal || 0).toLocaleString('en-IN'), 'kcal']);
  const elevOk = !NO_ELEVATION_TYPES.includes(a.type);
  if (elevOk && a.elevGain > 0) cells.push(['Elev gain', `+${Math.round(a.elevGain)}`, 'm']);
  if (elevOk && maxElev !== null) cells.push(['Max elevation', Math.round(maxElev), 'm']);
  const laps = swimLaps(a);
  if (laps) cells.push(['Laps', laps.laps, `× ${laps.pool} m`]);
  if (cells.length % 3 && a.startedAt) cells.push(['Started', formatTime12(a.startedAt), '']);
  return cells;
}

function titleCase(s) {
  return s ? s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : '';
}

/** Yoga details from activity_meta (YogaMeta in the app: style, intensity, focus_areas). */
export function yogaInfo(a) {
  const m = a.meta || {};
  const focus = Array.isArray(m.focus_areas)
    ? m.focus_areas.filter(Boolean).map((f) => titleCase(String(f).toLowerCase()))
    : [];
  return { style: titleCase(m.style) || 'Yoga', intensity: titleCase(m.intensity), focus };
}

const MOOD_WORDS = ['', 'Tough', 'Meh', 'OK', 'Good', 'Great'];

/**
 * "3 · OK" — the yoga mood before (activity_meta.mood_before, 1–5), labelled
 * like UpVibe's YogaMeta.moodLabel. Null when none was recorded.
 */
export function moodBefore(a) {
  if (a.type !== 'yoga') return null;
  const mood = Math.round(num((a.meta || {}).mood_before) || 0);
  return mood >= 1 && mood <= 5 ? `${mood} · ${MOOD_WORDS[mood]}` : null;
}

/** Pool laps when the app recorded a pool length. */
export function swimLaps(a) {
  const m = a.meta || {};
  const pool = num(m.pool_length_m ?? m.pool_length);
  if (!pool || !a.distanceKm) return null;
  return { laps: Math.round((a.distanceKm * 1000) / pool), pool };
}

/** True when the activity was recorded against a step goal (Walk only in the app). */
export function hasStepsGoal(a) {
  return a.type !== 'yoga' && a.targetSteps > 0;
}

/**
 * Steps for the detail view, mirroring UpVibe's ActivityDetailFragment: shown
 * for a steps-goal activity ("1,234 / 5,000"), whenever steps were recorded,
 * and always for Run/Walk/Hike (as "0") so the metric never silently vanishes.
 * Null when there is nothing to show.
 */
export function stepsInfo(a) {
  if (a.type === 'yoga') return null;
  const steps = Math.max(0, Math.round(a.totalSteps || 0));
  if (hasStepsGoal(a)) return { steps, target: Math.round(a.targetSteps) };
  if (steps > 0 || ['run', 'walk', 'hike'].includes(a.type)) return { steps, target: null };
  return null;
}

/**
 * Percentage of goal reached (0–100, capped like the app's goal ring): steps
 * against the step target when there is one, else distance against the
 * distance target. Null when the activity had no goal.
 */
export function goalPercent(a) {
  if (a.type === 'yoga') return null;
  let ratio = null;
  if (hasStepsGoal(a)) ratio = (a.totalSteps || 0) / a.targetSteps;
  else if (a.targetDistanceKm > 0) ratio = a.distanceKm / a.targetDistanceKm;
  return ratio === null ? null : Math.round(Math.min(Math.max(ratio, 0), 1) * 100);
}

/** The three headline numbers shown on a post: [{label, value, unit}]. */
export function headlineStats(a) {
  if (!hasDistance(a)) {
    const out = [];
    if (a.type === 'yoga') out.push({ label: 'Style', value: yogaInfo(a).style, unit: '' });
    out.push({ label: 'Time', value: formatDuration(a.durationSec), unit: '' });
    if (a.kcal) out.push({ label: 'Kcal', value: Math.round(a.kcal).toLocaleString('en-IN'), unit: '' });
    return out;
  }
  const stats = [
    { label: 'Distance', value: formatKm(a.distanceKm), unit: 'km' },
    { label: 'Time', value: formatDuration(a.durationSec), unit: '' },
  ];
  const ps = paceOrSpeed(a);
  // The post shows three numbers; a missing pace's "—" is noise there.
  if (ps && ps.value !== '—') stats.push(ps);
  return stats;
}

export function formatTime12(d) {
  let h = d.getHours();
  const ap = h < 12 ? 'AM' : 'PM';
  h = h % 12 || 12;
  return `${h}:${pad2(d.getMinutes())} ${ap}`;
}

/** "Thu, 24 Sep · 6:12 AM" — the UpVibe History format. */
export function formatDateTime(d) {
  return `${DOW[d.getDay()]}, ${d.getDate()} ${MON[d.getMonth()]} · ${formatTime12(d)}`;
}

export function formatShortDate(d) {
  return `${DOW[d.getDay()]}, ${d.getDate()} ${MON[d.getMonth()]}`;
}

export const dayLetter = (d) => DOW[d.getDay()][0];
export const monthShort = (d) => MON[d.getMonth()];

export function startOfWeek(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return d;
}

export function dayKey(d) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/** "21 – 27 Sep" / "31 Aug – 6 Sep" */
export function weekRange(monday) {
  const end = new Date(monday);
  end.setDate(end.getDate() + 6);
  const startMonth = monday.getMonth() !== end.getMonth() ? ` ${MON[monday.getMonth()]}` : '';
  return `${monday.getDate()}${startMonth} – ${end.getDate()} ${MON[end.getMonth()]}`;
}

export function weekName(monday, now = new Date()) {
  const diff = Math.round((startOfWeek(now) - monday) / (7 * DAY_MS));
  if (diff === 0) return 'This week';
  if (diff === 1) return 'Last week';
  return weekRange(monday);
}

/** Newest-first activities → [{ start, activities, km, sec }] newest week first. */
export function groupByWeek(list) {
  const groups = new Map();
  list.forEach((a) => {
    const k = startOfWeek(a.startedAt).getTime();
    if (!groups.has(k)) groups.set(k, []);
    groups.get(k).push(a);
  });
  return [...groups].map(([k, acts]) => ({
    start: new Date(k),
    activities: acts,
    km: acts.reduce((s, a) => s + a.distanceKm, 0),
    sec: acts.reduce((s, a) => s + a.durationSec, 0),
  }));
}

/**
 * Monday→Sunday of the current week, flagged from a list of active days
 * ('YYYY-MM-DD' in the viewer's zone — athlete_active_days returns exactly that).
 */
export function weekDays(activeDays, now = new Date()) {
  const active = new Set(activeDays);
  const monday = startOfWeek(now);
  const todayKey = dayKey(now);
  return Array.from({ length: 7 }, (_, i) => {
    const date = new Date(monday);
    date.setDate(date.getDate() + i);
    const key = dayKey(date);
    return {
      date,
      active: active.has(key),
      today: key === todayKey,
      future: date > now && key !== todayKey,
    };
  });
}

/** Consecutive active days ending today — or yesterday, so an unfinished today does not break it. */
export function currentStreak(activeDays, now = new Date()) {
  const days = new Set(activeDays);
  const cursor = new Date(now);
  if (!days.has(dayKey(cursor))) cursor.setDate(cursor.getDate() - 1);
  let streak = 0;
  while (days.has(dayKey(cursor))) {
    streak += 1;
    cursor.setDate(cursor.getDate() - 1);
  }
  return streak;
}

export function countsByType(list) {
  return list.reduce((m, a) => ({ ...m, [a.type]: (m[a.type] || 0) + 1 }), {});
}

export function matchesFilter(a, type, query) {
  if (type !== 'all' && a.type !== type) return false;
  if (!query) return true;
  const hay = `${displayTitle(a)} ${typeMeta(a.type).label} ${a.notes}`.toLowerCase();
  return hay.includes(query.trim().toLowerCase());
}

export const PRIVACY_LABEL = { private: 'Only you', followers: 'Followers', public: 'Public' };

/* ───────────────────────────── Route & elevation ───────────────────────────── */

const EARTH_M = 6371000;
const rad = (d) => (d * Math.PI) / 180;

export function haversineM(a, b) {
  const dLat = rad(b.lat - a.lat);
  const dLng = rad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Keep every nth point so long routes stay light, always keeping the last one. */
export function downsample(points, max) {
  if (points.length <= max) return points;
  const step = points.length / max;
  const out = [];
  for (let i = 0; i < max; i += 1) out.push(points[Math.floor(i * step)]);
  if (out[out.length - 1] !== points[points.length - 1]) out.push(points[points.length - 1]);
  return out;
}

/**
 * activity_gps_point rows → ordered route with cumulative distance.
 * Rows without a usable coordinate (0,0 or missing) are dropped; duplicates by
 * sequence keep the first seen, matching the app's own dedupe.
 */
export function routeFromPoints(rows, max = 800) {
  if (!Array.isArray(rows)) return [];
  const seen = new Set();
  const pts = rows
    .map((r) => ({ seq: num(r.sequence) ?? 0, lat: num(r.latitude), lng: num(r.longitude), alt: num(r.altitude) }))
    .filter((p) => p.lat !== null && p.lng !== null && !(p.lat === 0 && p.lng === 0)
      && Math.abs(p.lat) <= 90 && Math.abs(p.lng) <= 180)
    .sort((x, y) => x.seq - y.seq)
    .filter((p) => (seen.has(p.seq) ? false : seen.add(p.seq)));
  let total = 0;
  const withDist = pts.map((p, i) => {
    if (i) total += haversineM(pts[i - 1], p);
    return { ...p, distM: total };
  });
  const kept = downsample(withDist, max);
  // Max Elevation is read off this route, so the highest fix must survive the
  // downsample (and the route cache, which stores exactly what is kept here).
  const top = highestPoint(withDist);
  if (top && !kept.includes(top)) {
    const at = kept.findIndex((p) => p.seq > top.seq);
    kept.splice(at === -1 ? kept.length : at, 0, top);
  }
  return kept;
}

function highestPoint(pts) {
  return pts.reduce((best, p) => (p.alt !== null && (!best || p.alt > best.alt) ? p : best), null);
}

/** True when a device actually reported altitude: an all-zero series means it never did. */
function hasAltitude(route) {
  return route.some((p) => p.alt !== null && p.alt !== 0);
}

/**
 * Highest recorded altitude in metres, or null when none was recorded — the
 * app's Max Elevation (raw readings, not the smoothed chart line).
 */
export function maxElevation(route) {
  if (!Array.isArray(route) || !hasAltitude(route)) return null;
  return highestPoint(route).alt;
}

/**
 * The recorded point nearest to `distM` metres along the route (binary search
 * on the cumulative distance), or null for an empty route. The expanded map
 * uses it to place the cursor the elevation chart or replay points at.
 */
export function pointAtDistance(route, distM) {
  if (!Array.isArray(route) || !route.length) return null;
  let lo = 0;
  let hi = route.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (route[mid].distM < distM) lo = mid + 1; else hi = mid;
  }
  if (lo > 0 && Math.abs(route[lo - 1].distM - distM) <= Math.abs(route[lo].distM - distM)) lo -= 1;
  return route[lo];
}

/** The [lat, lng] positions between two distances along the route (a split's stretch). */
export function routeSlice(route, fromM, toM) {
  if (!Array.isArray(route)) return [];
  return route.filter((p) => p.distM >= fromM && p.distM <= toM).map((p) => [p.lat, p.lng]);
}

/** Half-width of the app's moving-average window over altitude (window = 5). */
const ELEV_SMOOTH_HALF = 2;

/**
 * Elevation profile from a route: [{ km, alt }] in metres above sea level, or
 * [] when altitude was not recorded. Mirrors the app's
 * BuildElevationProfileUseCase so both draw the same line: a missing reading
 * carries the last valid one (never read as sea level), the series is
 * smoothed with a 5-point moving average, and a route with no movement has
 * nothing to plot.
 */
export function elevationProfile(route, max = 160) {
  if (!Array.isArray(route) || route.length < 2 || !hasAltitude(route)) return [];
  const first = route.find((p) => p.alt !== null).alt;
  let last = first;
  const raw = route.map((p) => { if (p.alt !== null) last = p.alt; return last; });
  const smoothed = raw.map((_, i) => {
    const lo = Math.max(0, i - ELEV_SMOOTH_HALF);
    const hi = Math.min(raw.length - 1, i + ELEV_SMOOTH_HALF);
    let sum = 0;
    for (let j = lo; j <= hi; j += 1) sum += raw[j];
    return sum / (hi - lo + 1);
  });
  if (!(route[route.length - 1].distM > 0)) return [];
  const pts = route.map((p, i) => ({ km: p.distM / 1000, alt: smoothed[i] }));
  return downsample(pts, max);
}

/** Below this spread (m) the chart is not stretched, so GPS noise reads as flat. */
const ELEV_MIN_RANGE_M = 20;

/**
 * Y-axis window for the elevation chart, as the app draws it: 8% headroom
 * below, 12% above, floored at 20 m and re-centred on the data so a flat
 * route renders flat. Returns { min, max } in metres above sea level.
 */
export function elevationWindow(alts) {
  const lo = Math.min(...alts);
  const hi = Math.max(...alts);
  const range = hi - lo;
  let min = lo - range * 0.08;
  let span = range * 1.2;
  if (span < ELEV_MIN_RANGE_M) {
    min = lo + range / 2 - ELEV_MIN_RANGE_M / 2;
    span = ELEV_MIN_RANGE_M;
  }
  return { min, max: min + span };
}

/** Clean axis bounds for a chart: { max, step } with 2–4 steps. */
export function niceScale(value) {
  const steps = [0.5, 1, 2, 2.5, 5, 10, 20, 25, 50, 100, 200, 250, 500];
  const v = Math.max(value, 0.0001);
  for (const s of steps) {
    if (v / s <= 4) return { max: Math.max(s, Math.ceil(v / s) * s), step: s };
  }
  const s = 10 ** Math.ceil(Math.log10(v / 4));
  return { max: Math.ceil(v / s) * s, step: s };
}
